import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../../api';
import { admissionEndpoints } from '../../api';
import { Badge, Button, EmptyRow, Panel, Table } from '../../ui';
import { INPUT } from '../event/shared';
import { dateWords } from '../../terms';

/**
 * Gates and access zones for a venue (docs/scanner.md §1, D-59). A gate is
 * where a scanner stands; a zone is an area a ticket may enter. Which zones a
 * gate admits, and which tickets belong to which zone, are set per
 * performance on the Door page. With nothing set, every gate admits every
 * ticket.
 */
export function GatesPanel({ venueId, canConfigure, onError }: { venueId: string; canConfigure: boolean; onError: (message: string) => void }) {
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: ['admissions', 'venue', venueId], queryFn: () => admissionEndpoints.venue(venueId) });
  const [gate, setGate] = useState({ code: '', name: '' });
  const [zone, setZone] = useState({ code: '', name: '' });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admissions', 'venue', venueId] });
  const fail = (cause: ApiError) => onError(cause.message);
  const createGate = useMutation({ mutationFn: () => admissionEndpoints.createGate(venueId, { code: gate.code.trim().toUpperCase(), name: gate.name.trim() }), onSuccess: () => { setGate({ code: '', name: '' }); refresh(); }, onError: fail });
  const createZone = useMutation({ mutationFn: () => admissionEndpoints.createZone(venueId, { code: zone.code.trim().toUpperCase(), name: zone.name.trim() }), onSuccess: () => { setZone({ code: '', name: '' }); refresh(); }, onError: fail });
  const toggleGate = useMutation({ mutationFn: ({ id, active }: { id: string; active: boolean }) => admissionEndpoints.updateGate(id, { active }), onSuccess: refresh, onError: fail });
  const toggleZone = useMutation({ mutationFn: ({ id, active }: { id: string; active: boolean }) => admissionEndpoints.updateZone(id, { active }), onSuccess: refresh, onError: fail });

  const form = (state: { code: string; name: string }, set: (s: { code: string; name: string }) => void, submit: () => void, label: string) =>
    canConfigure ? (
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input className={`${INPUT} w-28 font-mono uppercase`} placeholder="CODE" value={state.code} onChange={(e) => set({ ...state, code: e.target.value })} aria-label={`${label} code`} required />
        <input className={`${INPUT} w-48`} placeholder="Name" value={state.name} onChange={(e) => set({ ...state, name: e.target.value })} aria-label={`${label} name`} required />
        <Button type="submit" variant="primary">
          Add {label}
        </Button>
      </form>
    ) : null;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="Gates" description="Where scanners stand. Scanner staff pick one at setup; the Door page shows admissions by gate." actions={form(gate, setGate, () => createGate.mutate(), 'gate')}>
        <Table headers={['Gate', 'Code', 'Status', '']}>
          {(data.data?.gates ?? []).length === 0 ? (
            <EmptyRow columns={4}>No gates yet. Scanners can still scan with "Any gate".</EmptyRow>
          ) : (
            data.data!.gates.map((g) => (
              <tr key={g.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-ink-900">{g.name}</td>
                <td className="px-5 py-3 font-mono text-xs text-ink-600">{g.code}</td>
                <td className="px-5 py-3">
                  <Badge tone={g.active ? 'ok' : 'neutral'}>{g.active ? 'active' : 'inactive'}</Badge>
                </td>
                <td className="px-5 py-3 text-right">{canConfigure ? <Button onClick={() => toggleGate.mutate({ id: g.id, active: !g.active })}>{g.active ? 'Deactivate' : 'Activate'}</Button> : null}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
      <Panel title="Access zones" description={`Areas a ticket may enter (General, VIP lounge, Floor…). Per ${dateWords().one}, the Door page says which gates admit which zones and which tickets belong to them.`} actions={form(zone, setZone, () => createZone.mutate(), 'zone')}>
        <Table headers={['Zone', 'Code', 'Status', '']}>
          {(data.data?.zones ?? []).length === 0 ? (
            <EmptyRow columns={4}>No zones yet. Without zones every gate admits every ticket.</EmptyRow>
          ) : (
            data.data!.zones.map((z) => (
              <tr key={z.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-ink-900">{z.name}</td>
                <td className="px-5 py-3 font-mono text-xs text-ink-600">{z.code}</td>
                <td className="px-5 py-3">
                  <Badge tone={z.active ? 'ok' : 'neutral'}>{z.active ? 'active' : 'inactive'}</Badge>
                </td>
                <td className="px-5 py-3 text-right">{canConfigure ? <Button onClick={() => toggleZone.mutate({ id: z.id, active: !z.active })}>{z.active ? 'Deactivate' : 'Activate'}</Button> : null}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}
